import { type ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

// Concise, TSX-authored version of the consumer guides (NOT an iframe). These mirror the
// hand-authored static docs under priv/static/docs and link out to the full reference. Content is
// verified against contracts/responses.v1.yaml and the routing/override modules — no invented
// fields. The full guide pages live at /docs/<page>.html (the Phoenix-served site).

interface GuideSectionProps {
  id: string;
  title: string;
  href: string;
  hrefLabel: string;
  children: ReactNode;
}

function GuideSection({ id, title, href, hrefLabel, children }: GuideSectionProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <section
      id={id}
      aria-labelledby={`${id}-heading`}
      className={`rounded-2xl border p-6 ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-white/[0.01]'
          : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3
          id={`${id}-heading`}
          className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {title}
        </h3>
        <a
          href={href}
          className={`inline-flex flex-shrink-0 items-center gap-1.5 text-xs font-medium ${
            isDark ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-500'
          }`}
        >
          {hrefLabel}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      </div>
      <div
        className={`space-y-3 text-sm leading-relaxed ${
          isDark ? 'text-white/60' : 'text-slate-600'
        }`}
      >
        {children}
      </div>
    </section>
  );
}

function Mono({ children }: { children: ReactNode }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <code
      className={`rounded px-1.5 py-0.5 font-mono text-[0.8em] ${
        isDark ? 'bg-white/[0.06] text-indigo-200' : 'bg-slate-100 text-indigo-700'
      }`}
    >
      {children}
    </code>
  );
}

export function DocsGuides() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <GuideSection
        id="guide-getting-started"
        title="Getting started"
        href="/docs/index.html"
        hrefLabel="Full guide"
      >
        <p>
          Call <Mono>POST /v1/responses</Mono> with a Bearer <Mono>sk-proxy-…</Mono> key. The body
          needs a canonical <Mono>model</Mono> and an <Mono>input</Mono> (string or typed items).
          Retrieve with <Mono>GET /v1/responses/&#123;id&#125;</Mono> and cancel with{' '}
          <Mono>POST /v1/responses/&#123;id&#125;/cancel</Mono>.
        </p>
        <p>
          The response shape is identical no matter which backend served it; the routing decision is
          exposed under <Mono>x_proxy</Mono>.
        </p>
      </GuideSection>

      <GuideSection
        id="guide-streaming"
        title="Streaming"
        href="/docs/streaming.html"
        hrefLabel="Full guide"
      >
        <p>
          Set <Mono>stream: true</Mono> to receive Server-Sent Events instead of a single JSON
          object. Events follow the Responses taxonomy: <Mono>response.created</Mono>,{' '}
          <Mono>response.output_text.delta</Mono>, then a terminal{' '}
          <Mono>response.completed</Mono> (or <Mono>response.error</Mono> on failure).
        </p>
      </GuideSection>

      <GuideSection
        id="guide-routing"
        title="Routing & providers"
        href="/docs/routing.html"
        hrefLabel="Full guide"
      >
        <p>
          Policy precedence is <Mono>app + model</Mono> &gt; <Mono>app</Mono> &gt;{' '}
          <Mono>model</Mono> &gt; <Mono>global</Mono>. Within a policy, providers are tried in order,
          skipping any that are unhealthy or saturated; dispatch then falls back within a bounded
          budget (exhausted → <Mono>502</Mono>).
        </p>
        <p>
          Read <Mono>x_proxy.routed_reason</Mono> to see why a backend won:{' '}
          <Mono>preferred</Mono>, <Mono>overflow</Mono>, <Mono>unhealthy_failover</Mono>,{' '}
          <Mono>override</Mono>, or <Mono>retry_fallback</Mono>.
        </p>
      </GuideSection>

      <GuideSection
        id="guide-forcing"
        title="Forcing behavior"
        href="/docs/forcing-behavior.html"
        hrefLabel="Full guide"
      >
        <p>
          Operators set a per-key <Mono>override_mode</Mono>: <Mono>auto</Mono>,{' '}
          <Mono>force_internal</Mono>, <Mono>force_external</Mono>, or <Mono>pin</Mono> (+{' '}
          <Mono>pinned_provider</Mono>). A <Mono>force_internal</Mono> or in-house-pinned key is
          residency-locked &mdash; it <strong>never</strong> egresses offshore, even under failover
          (it fails with <Mono>502</Mono> instead).
        </p>
        <p>
          App/model-scoped routing policies and model aliases (canonical → per-provider models) are
          the other two levers. The panel below shows the live alias map.
        </p>
      </GuideSection>

      <GuideSection
        id="guide-errors"
        title="Errors, quotas & rate limits"
        href="/docs/errors.html"
        hrefLabel="Full guide"
      >
        <p>
          Failures use a single <Mono>&#123;error: &#123;type, code, message&#125;&#125;</Mono>{' '}
          envelope. Status matrix: <Mono>400</Mono>/<Mono>401</Mono>/<Mono>404</Mono>/
          <Mono>429</Mono>/<Mono>502</Mono> on create, plus <Mono>409</Mono> on cancel. Honour{' '}
          <Mono>Retry-After</Mono> on a <Mono>429</Mono>.
        </p>
        <p>Quotas apply at three scopes: key, app, and client.</p>
      </GuideSection>

      <GuideSection
        id="guide-reference"
        title="API reference"
        href="/docs/api-reference.html"
        hrefLabel="Open reference"
      >
        <p>
          The full machine-readable contract is rendered from the frozen OpenAPI spec at{' '}
          <Mono>/docs/api-reference.html</Mono> &mdash; every endpoint, schema, and status code.
        </p>
      </GuideSection>
    </div>
  );
}
